// MembershipPlansManagement.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaEdit, FaTrash } from "react-icons/fa";

const MembershipPlansManagement = () => {
  const { user, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newPlan, setNewPlan] = useState({
    name: "",
    duration: "",
    price: "",
    totalPrice: "",
    discount: "",
    benefits: [],
  });
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const getRoleForPlan = (planName) => {
    switch (planName.toLowerCase()) {
      case "vàng":
        return "vip_gold";
      case "bạch kim":
        return "vip_platinum";
      case "kim cương":
        return "vip_diamond";
      default:
        return "Không xác định";
    }
  };

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({ method: "get", url: "/api/admin/coin-to-vip-plans" });
      // Đảm bảo các giá trị không phải là null
      const sanitizedPlans = res.data.map(plan => ({
        ...plan,
        duration: plan.duration ?? 0,
        price: plan.price ?? 0,
        totalPrice: plan.totalPrice ?? 0,
        discount: plan.discount ?? 0,
        benefits: Array.isArray(plan.benefits) ? plan.benefits : [],
      }));
      setPlans(sanitizedPlans);
      setLoading(false);
    } catch (error) {
      setError("Không thể tải danh sách gói nâng cấp!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const handleAddPlan = async () => {
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/membership-plans",
        data: {
          ...newPlan,
          duration: parseInt(newPlan.duration) || 0,
          price: parseInt(newPlan.price) || 0,
          totalPrice: parseInt(newPlan.totalPrice) || 0,
          discount: parseInt(newPlan.discount) || 0,
        },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setNewPlan({ name: "", duration: "", price: "", totalPrice: "", discount: "", benefits: [] });
      setShowAddModal(false);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể thêm gói!");
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/membership-plans/${editingPlan._id}`,
        data: {
          name: editingPlan.name,
          duration: parseInt(editingPlan.duration) || 0,
          price: parseInt(editingPlan.price) || 0,
          totalPrice: parseInt(editingPlan.totalPrice) || 0,
          discount: parseInt(editingPlan.discount) || 0,
          benefits: editingPlan.benefits,
        },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingPlan(null);
      setShowEditModal(false);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể cập nhật gói!");
    }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xóa gói này?")) return;
    try {
      const response = await fetchWithRetry({
        method: "delete",
        url: `/api/admin/membership-plans/${id}`,
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPlans();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể xóa gói!");
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadPlans();
  }, [user, navigate, loadPlans, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý gói nâng cấp</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Thêm gói mới
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}

      <div className="overflow-x-auto">
        <table className="w-full bg-white rounded-lg shadow-lg">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="p-4 text-left">Tên gói</th>
              <th className="p-4 text-left">Vai trò</th>
              <th className="p-4 text-left">Thời hạn (tháng)</th>
              <th className="p-4 text-left">Giá (VND/tháng)</th>
              <th className="p-4 text-left">Tổng giá (VND)</th>
              <th className="p-4 text-left">Tiết kiệm (%)</th>
              <th className="p-4 text-left">Số coin cần thiết</th>
              <th className="p-4 text-left">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan._id} className="border-b hover:bg-gray-50">
                <td className="p-4">{plan.name}</td>
                <td className="p-4">{getRoleForPlan(plan.name)}</td>
                <td className="p-4">{plan.duration}</td>
                <td className="p-4">{plan.price.toLocaleString()}</td>
                <td className="p-4">{plan.totalPrice.toLocaleString()}</td>
                <td className="p-4">{plan.discount}</td>
                <td className="p-4">{plan.coinPrice ? plan.coinPrice.toLocaleString() : "Chưa thiết lập"}</td>
                <td className="p-4 space-x-2">
                  <button
                    onClick={() => {
                      setEditingPlan(plan);
                      setShowEditModal(true);
                    }}
                    className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan._id)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Thêm gói mới</h3>
            <input
              type="text"
              placeholder="Tên gói (Vàng, Bạch Kim, Kim Cương)"
              value={newPlan.name}
              onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <p className="text-sm text-gray-600 mb-2">Vai trò: {getRoleForPlan(newPlan.name)}</p>
            <input
              type="number"
              placeholder="Thời hạn (tháng)"
              value={newPlan.duration}
              onChange={(e) => setNewPlan({ ...newPlan, duration: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Giá (VND/tháng)"
              value={newPlan.price}
              onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Tổng giá (VND)"
              value={newPlan.totalPrice}
              onChange={(e) => setNewPlan({ ...newPlan, totalPrice: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Tiết kiệm (%)"
              value={newPlan.discount}
              onChange={(e) => setNewPlan({ ...newPlan, discount: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <textarea
              placeholder="Quyền lợi (mỗi dòng một quyền lợi)"
              value={newPlan.benefits.join("\n")}
              onChange={(e) => setNewPlan({ ...newPlan, benefits: e.target.value.split("\n") })}
              className="w-full p-2 mb-3 border rounded"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handleAddPlan}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Chỉnh sửa gói</h3>
            <input
              type="text"
              placeholder="Tên gói (Vàng, Bạch Kim, Kim Cương)"
              value={editingPlan.name ?? ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <p className="text-sm text-gray-600 mb-2">Vai trò: {getRoleForPlan(editingPlan.name)}</p>
            <input
              type="number"
              placeholder="Thời hạn (tháng)"
              value={editingPlan.duration ?? ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, duration: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Giá (VND/tháng)"
              value={editingPlan.price ?? ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, price: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Tổng giá (VND)"
              value={editingPlan.totalPrice ?? ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, totalPrice: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <input
              type="number"
              placeholder="Tiết kiệm (%)"
              value={editingPlan.discount ?? ""}
              onChange={(e) => setEditingPlan({ ...editingPlan, discount: e.target.value })}
              className="w-full p-2 mb-3 border rounded"
            />
            <textarea
              placeholder="Quyền lợi (mỗi dòng một quyền lợi)"
              value={editingPlan.benefits.join("\n")}
              onChange={(e) => setEditingPlan({ ...editingPlan, benefits: e.target.value.split("\n") })}
              className="w-full p-2 mb-3 border rounded"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdatePlan}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPlansManagement;